import { ChainId } from '@pancakeswap/sdk'
import type { Address } from 'viem'
import type { DexId } from './types'

/**
 * Per-chain enablement. Only listed adapters are instantiated; a chain with no entry hides the
 * MultiSwap page entirely (brief §3: unconnected options are not shown).
 */
export const MULTISWAP_ENABLED: Partial<Record<number, DexId[]>> = {
  [ChainId.BSC]: ['cadinu', 'pancakeswap', 'uniswap', 'biswap', 'bakeryswap'],
}

/**
 * Quote lifetime (TTL). After this the quote is shown as expired and cannot be executed. Until
 * 2026-09-28 this was the only timer: nothing refetched by itself, so a user who paused for 30 s
 * always hit EXPIRED + manual refresh. The refresh period below is independent of it.
 */
export const QUOTE_TTL_MS = 30_000
/**
 * Auto-refresh period (brief §5): the quote is refetched every QUOTE_REFRESH_MS while the tab is
 * visible and pair + amount are valid (multiswap/autoRefresh.ts). Override with
 * NEXT_PUBLIC_QUOTE_REFRESH_MS (build time); must be shorter than the TTL.
 */
export const QUOTE_REFRESH_MS = (() => {
  const v = Number(process.env.NEXT_PUBLIC_QUOTE_REFRESH_MS)
  return Number.isFinite(v) && v >= 3_000 && v < QUOTE_TTL_MS ? v : 15_000
})()
/** Quiet time after an input change before the first refresh. */
export const QUOTE_INPUT_DEBOUNCE_MS = 600
/** Per-source timeout; a slow source is reported as `timeout` and the other is still shown. */
export const SOURCE_TIMEOUT_MS = 12_000
/** Output or route change beyond this (bips) between the selected quote and a refresh re-opens review. */
export const REQUOTE_THRESHOLD_BIPS = 50n

/**
 * PancakeSwap BSC mainnet — official docs (developer.pancakeswap.finance/contracts/{v2,v3}/addresses,
 * fetched 2026-09-28) and verified on-chain the same day; see docs/MULTISWAP-CONTRACTS.md §2.
 */
export const PANCAKESWAP_BSC = {
  v2Factory: '0xcA143Ce32Fe78f1f7019d7d551a6402fC5350c73' as Address,
  v2Router02: '0x10ED43C718714eb63d5aA57B78B54704E256024E' as Address,
  v3Factory: '0x0BFbCF9fa4f9C56B0F40a671Ad40E0805A091865' as Address,
  v3SwapRouter: '0x1b81D678ffb9C0263b24A97847620C99d213eB14' as Address,
  quoterV2: '0xB048Bbc1Ee6b733FFfCFb9e9CeF7375518e25997' as Address,
  feeTiers: [100, 500, 2500, 10000] as const,
}

export const WBNB_BSC: Address = '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c'

/**
 * The other exchanges the V4 trading competition accepts (views/TradingCompetitionV3/config.ts DEXES), each traded through ITS OWN
 * router: no aggregator contract, no split, no new contract (2026-10-06). Every router below is BscScan-verified source (Exact Match)
 * and `router.factory()` / `router.WETH()` (BakerySwap: `WBNB()`) were read on chain on 2026-10-06; the live test
 * fork/venues.live.test.ts re-reads them and evidence/stage-multiswap-8dex-20261006/NOTE.md holds the table.
 */
export const UNISWAP_BSC = {
  v2Factory: '0x8909Dc15e40173Ff4699343b6eB8132c65e18eC6' as Address,
  v2Router02: '0x4752ba5DBc23f44D87826276BF6Fd6b1C372aD24' as Address,
  v3Factory: '0xdB1d10011AD0Ff90774D0C6Bb92e5C5c8b4461F7' as Address,
  /** SwapRouter02: exactInput WITHOUT a deadline in the struct; the deadline is multicall(uint256 deadline, bytes[]). */
  swapRouter02: '0xB971eF87ede563556b2ED4b1C0b0019111Dd85d2' as Address,
  quoterV2: '0x78D78E420Da98ad378D7799bE8f4AF69033EB077' as Address,
  feeTiers: [100, 500, 3000, 10000] as const,
  /** UniswapV2Library: amountIn × 997 / 1000. */
  v2FeeBps: 30,
}

export const BISWAP_BSC = {
  factory: '0x858E3312ed3A876947EA49d572A7C42DE08af7EE' as Address,
  router: '0x3a6d8cA21D1CF76F653A67577FA0D27453350dD8' as Address,
  // the LP fee is stored PER PAIR (pair.swapFee(), thousandths: 1 = 0.1 %); the router's getAmountsOut reads it, so do we
}

export const BAKERYSWAP_BSC = {
  factory: '0x01bF7C66c6BD861915CdaaE475042d3c4BaE16A7' as Address,
  /** Native entry points are named swapExactBNBForTokens / swapExactTokensForBNB, and the wrapped-native getter is WBNB(). */
  router: '0xCDe540d7eAFE93aC5fE6233Bee57E1270D3E330F' as Address,
  /** BakerySwapLibrary: amountIn × 997 / 1000. */
  feeBps: 30,
}

/** Display names, one per source (an adapter may cover a V2 and a V3 router of the same exchange). */
export const DEX_NAMES: Record<DexId, string> = {
  cadinu: 'CADINUdex',
  pancakeswap: 'PancakeSwap',
  uniswap: 'Uniswap',
  biswap: 'Biswap',
  bakeryswap: 'BakerySwap',
}

/**
 * CADINU protocol fee on the output (brief §5, Round 2: stage only). Unit: basis points of the
 * output — the Smart Router computes `balance * feeBips / 10_000` and requires `0 < feeBips <= 100`
 * (source/contracts/verified/SmartRouter/SmartRouter.sol:3876 sweepTokenWithFee, :3854
 * unwrapWETH9WithFee), so 1 = 0.01 %. The recipient is a per-call parameter (`feeRecipient`), not
 * a contract setting. Both env values are read at build time; unset/invalid → no fee shown or taken.
 */
export interface ProtocolFeeConfig {
  bips: number
  recipient: Address
}

export function parseFeeConfig(env: { bips?: string; recipient?: string }): ProtocolFeeConfig | null {
  const bips = Number(env.bips ?? '0')
  const recipient = (env.recipient ?? '').trim()
  if (!Number.isInteger(bips) || bips <= 0 || bips > 100) return null
  if (!/^0x[0-9a-fA-F]{40}$/.test(recipient) || /^0x0{40}$/.test(recipient)) return null
  return { bips, recipient: recipient as Address }
}

export const PROTOCOL_FEE: ProtocolFeeConfig | null = parseFeeConfig({
  bips: process.env.NEXT_PUBLIC_MULTISWAP_FEE_BIPS,
  recipient: process.env.NEXT_PUBLIC_MULTISWAP_FEE_RECIPIENT,
})

/**
 * Tokens that take a fee on transfer (or otherwise break "amount sent = amount received").
 * MultiSwap never quotes them: both adapters build the plain `swapExactTokensForTokens` /
 * `exactInput` path and the quote assumes the full input arrives (docs/MULTISWAP-SIMULATION.md).
 * Source: apps/web/src/config/constants/swapWarningTokens.ts (safemoon, bondly, itam, ccar, bttold,
 * pokemoney, free, gala, abnbc, xcad) — kept as addresses so this module has no React imports.
 */
export const FEE_ON_TRANSFER_TOKENS: ReadonlySet<string> = new Set(
  [
    '0x8076C74C5e3F5852037F31Ff0093Eeb8c8ADd8D3', // SAFEMOON
    '0x96058f8C3e16576D9BD68766f3836d9A33158f89', // BONDLY
    '0x04C747b40Be4D535fC83D09939fb0f626F32800B', // ITAM
    '0x50332bdca94673F33401776365b66CC4e81aC81d', // CCAR
    '0x8595F9dA7b868b1822194fAEd312235E43007b49', // BTTOLD
    '0x32ff5b4C3B1744F0344D96fA2bC1cC8DD5b6dfb0', // POKEMONEY
    '0x880BCe9321c79cAc1D290De6d31DDE722C606165', // FREE
    '0x7dDEE176F665cD201F93eEDE625770E2fD911990', // GALA
    '0xE4FD2bE72eE64e77cC55Cc8f7F9df2F09E5C4C75', // ABNBC
    '0x431e0cD023a32532BF3969CddFc002c00E98429d', // XCAD
  ].map((a) => a.toLowerCase()),
)

/**
 * Listed tokens whose transfer-tax check is skipped (Bez 2026-10-07): a small fixed set of known clean majors. Every other listed token
 * is checked on chain like an imported one (multiswap/importedCheck.ts), in parallel with quoting. Each of these was measured untaxed
 * by the same probe on 2026-10-07 (report 60, "Listed tokens"); a token joins this set only after such a measurement.
 */
export const LISTED_CHECK_SKIP: ReadonlySet<string> = new Set(
  [
    '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c', // WBNB
    '0x55d398326f99059fF775485246999027B3197955', // USDT
    '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', // USDC
    '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56', // BUSD
    '0x76e112203eF59D445452ef7556386dD2DF3Ed914', // CADINU
    '0x6e64fCF15Be3eB71C3d42AcF44D85bB119b2D98b', // CBON
    '0x7130d2A12B9BCbFAe4f2634d864A1Ee1Ce3Ead9c', // BTCB
    '0x2170Ed0880ac9A755fd29B2688956BD959F933F8', // ETH (Binance-Peg)
  ].map((a) => a.toLowerCase()),
)
